import {
    BudgetProposalResourceMetadataT,
    BudgetProposalT,
    StatusesSettingsT,
} from 'routes/BudgetModule/config/types';
import { ColDef } from 'ag-grid-community';
import {
    getAmountColumn,
    getCommentColumn,
    getDetailsColumn,
    getProjectColumn,
    getStatusColumn,
} from 'routes/BudgetModule/components/shared/InscriptionsGrid/gridHelpers';
import {
    CHAPTER_SOFTWARE_ID,
    NATURE_SOFTWARE_ID,
    SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID,
} from 'routes/BudgetModule/config/constants';
import { TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';
import { translateBlock } from 'i18n';
import { isCustomBudgetProposal } from '../../../../hooks/budgetProposal';
import { InscriptionT } from 'routes/BudgetModule/shared/entities/inscription';
import { SecondaryBudgetAxisSources } from 'routes/Support/components/ClientSupport/components/BudgetConfiguration/types';
import { IntegrationT } from 'routes/IntegrationsList/types';

export const SECONDARY_BUDGET_AXIS_ELEMENT_COL = 'secondaryBudgetAxisElement';

export const getOrphanInscriptionsGridColumns = (
    budgetProposal: BudgetProposalT,
    t: TranslationT,
    budgetProposals: BudgetProposalT[],
    statusesSettings: StatusesSettingsT,
    allowInscriptionSelection: boolean,
    showOperationColumn: boolean,
    showChapterColumn: boolean,
    resourceName: string,
    projects: Project[],
    integrations: IntegrationT[],
    sourceTable: SecondaryBudgetAxisSources | null,
    inscriptionsQueryCacheKey?: InscriptionQueryKey,
): ColDef[] => {
    const { name: budgetProposalName, phase } = budgetProposal;
    const isSchool = isCustomBudgetProposal(integrations);
    const trackingMetadata: BudgetProposalResourceMetadataT = {
        budgetProposalName,
        phase,
        resourceName,
    };
    const deleteButtonColumn = {
        field: 'deleteButton',
        headerName: '',
        sortable: false,
        resizable: true,
        width: 40,
        cellRenderer: 'deleteButtonCellRenderer',
        suppressHeaderMenuButton: true,
        editable: false,
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        suppressMovable: true,
    };
    const classifyButtonColumn = {
        field: 'classifyButton',
        headerName: '',
        sortable: false,
        resizable: true,
        width: 95,
        cellRenderer: 'classifyButtonCellRenderer',
        valueGetter: ({ data }: { data: { inscription: InscriptionT } }) => {
            if (!data) return null;
            return {
                SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID:
                    data.inscription[SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID],
                CHAPTER_SOFTWARE_ID: data.inscription[CHAPTER_SOFTWARE_ID],
                NATURE_SOFTWARE_ID: data.inscription[NATURE_SOFTWARE_ID],
            };
        },
        cellRendererParams: {
            trackingInformation: {
                eventName:
                    TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome
                        .imputations.classifyOrphan,
                metadata: trackingMetadata,
            },
        },
        suppressHeaderMenuButton: true,
        editable: false,
        suppressNavigable: true,
        suppressColumnsToolPanel: true,
        suppressMovable: true,
    };
    const columns: ColDef[] = [
        classifyButtonColumn,
        getDetailsColumn(allowInscriptionSelection),
        getStatusColumn(t, budgetProposals),
        getAmountColumn(
            t,
            budgetProposals,
            statusesSettings,
            budgetProposal.exercice,
            phase,
        ),
        getCommentColumn(t, budgetProposals, statusesSettings, phase),
        getProjectColumn(t, projects),
    ];
    if (showOperationColumn) {
        columns.push({
            field: SECONDARY_BUDGET_AXIS_ELEMENT_COL,
            headerName: translateBlock(sourceTable),
            valueGetter: ({ data }) => {
                if (!data) return null;
                const operationSoftwareId =
                    data.inscription[SECONDARY_BUDGET_AXIS_ELEMENT_SOFTWARE_ID];
                return operationSoftwareId;
            },
            sortable: true,
            resizable: true,
            cellRenderer: 'operationCellRenderer',
            cellRendererParams: {
                trackingInformation: {
                    eventName:
                        TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome
                            .imputations.addOrphanOperation,
                    metadata: trackingMetadata,
                },
                queryCacheKey: inscriptionsQueryCacheKey,
            },
        });
    }

    if (showChapterColumn && !isSchool) {
        columns.push({
            field: 'chapter',
            headerName: t('budgetProposal.chapter'),
            valueGetter: ({ data }) => {
                if (!data) return null;
                return data.inscription[CHAPTER_SOFTWARE_ID];
            },
            sortable: true,
            resizable: true,
            width: 200,
            cellRenderer: 'chapterCellRenderer',
            cellRendererParams: {
                trackingInformation: {
                    eventName:
                        TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome
                            .imputations.addOrphanChapter,
                    metadata: trackingMetadata,
                },
                queryCacheKey: inscriptionsQueryCacheKey,
            },
        });
        columns.push({
            field: 'nature',
            headerName: t('budgetProposal.nature'),
            valueGetter: ({ data }) => {
                if (!data) return null;
                return data.inscription[NATURE_SOFTWARE_ID];
            },
            sortable: true,
            resizable: true,
            width: 200,
            cellRenderer: 'natureCellRenderer',
            cellRendererParams: {
                trackingInformation: {
                    eventName:
                        TRACKABLE_EVENTS.budget.budgetProposal.expenseIncome
                            .imputations.addOrphanNature,
                    metadata: trackingMetadata,
                },
                queryCacheKey: inscriptionsQueryCacheKey,
            },
        });
    }

    return [...columns, deleteButtonColumn];
};
